import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmt } from "@/api";
import ProductCard from "@/components/ProductCard";
import { Sparkles, ShieldCheck, Truck, RotateCcw, HandCoins, Star } from "lucide-react";
import KitFinderModal from "@/components/KitFinderModal";

const HERO_BG = "https://images.unsplash.com/photo-1619898383579-fb0a52a93947?w=1200&q=80";
const FESTIVALS = [
  { name: "Diwali", slug:"Diwali", image:"https://images.unsplash.com/photo-1619898383579-fb0a52a93947?w=600&q=80" },
  { name: "Navratri", slug:"Navratri", image:"https://images.unsplash.com/photo-1620005807545-2e08850d6591?w=600&q=80" },
  { name: "Ganesh Chaturthi", slug:"Ganesh Chaturthi", image:"https://images.unsplash.com/photo-1567636788276-40a47795ba4d?w=600&q=80" },
  { name: "Shivratri", slug:"Shivratri", image:"https://images.unsplash.com/photo-1609619385076-36a873425636?w=600&q=80" },
  { name: "Karwa Chauth", slug:"Karwa Chauth", image:"https://images.unsplash.com/photo-1610375461369-d613b564f4c4?w=600&q=80" },
  { name: "Janmashtami", slug:"Janmashtami", image:"https://images.unsplash.com/photo-1615461066841-6116e61058f4?w=600&q=80" },
];
const DEITIES = [
  { name:"Shri Ganesh", slug:"Ganesh" },
  { name:"Maa Lakshmi", slug:"Lakshmi" },
  { name:"Bhagwan Shiva", slug:"Shiva" },
  { name:"Hanuman Ji", slug:"Hanuman" },
  { name:"Shri Krishna", slug:"Krishna" },
  { name:"Maa Durga", slug:"Durga" },
  { name:"Shri Ram", slug:"Ram" },
  { name:"Maa Saraswati", slug:"Saraswati" },
];

export default function Home() {
  const [cats, setCats] = useState([]);
  const [bestsellers, setBest] = useState([]);
  const [daily, setDaily] = useState([]);
  const [finderOpen, setFinderOpen] = useState(false);
  const [kow, setKow] = useState(null);

  useEffect(() => {
    api.get("/categories").then(({data}) => setCats(data));
    api.get("/products?best_seller=true&limit=12").then(({data}) => setBest(data.slice(0,10)));
    api.get("/products?category=puja-samagri&limit=8").then(({data}) => setDaily(data));
    api.get("/kit-of-week").then(({data}) => setKow(data)).catch(()=>{});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden hero-radial grain-overlay">
        <div className="max-w-7xl mx-auto px-4 pt-14 pb-16 md:pt-20 md:pb-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="section-label mb-4">A trusted spiritual store · Since 2025</div>
            <h1 className="devanagari text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-primary" style={{fontFamily:"'Noto Sans Devanagari','Playfair Display',serif"}}>
              पूजा की हर ज़रूरत,<br/><span className="text-accent">एक ही जगह।</span>
            </h1>
            <p className="mt-5 text-lg text-muted-foreground max-w-lg leading-relaxed">
              Everything you need for your puja — samagri, kits, idols, havan & more — hand-picked, purity-checked, and delivered to your door.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/category/puja-samagri" data-testid="hero-shop-samagri" className="pk-btn-primary">Shop Puja Samagri</Link>
              <Link to="/kits" data-testid="hero-explore-kits" className="pk-btn-secondary">Explore Puja Kits</Link>
              <button data-testid="hero-find-kit" onClick={()=>setFinderOpen(true)} className="pk-btn-accent"><Sparkles className="w-4 h-4"/>Find My Puja Kit</button>
            </div>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary"/>Purity Checked</span>
              <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-primary"/>Pan-India Delivery</span>
              <span className="flex items-center gap-2"><HandCoins className="w-4 h-4 text-primary"/>COD Available</span>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full bg-secondary/25 blur-2xl"></div>
            <div className="absolute -bottom-6 -left-6 w-40 h-40 rounded-full bg-accent/20 blur-2xl"></div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] border-4 border-white shadow-2xl">
              <img src={HERO_BG} alt="Puja" className="w-full h-full object-cover"/>
              <div className="absolute bottom-4 left-4 right-4 bg-white/85 backdrop-blur-md rounded-2xl p-4 shadow-lg border border-white">
                <div className="flex items-center gap-2 text-xs text-primary font-semibold uppercase tracking-widest"><Sparkles className="w-3 h-3"/>Kit of the Week</div>
                <div className="font-serif text-lg mt-1">Ganesh Puja Complete Kit</div>
                <div className="text-sm text-muted-foreground">For 3–5 people · 16 items</div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-lg font-semibold text-primary">{fmt(1399)}</span>
                  <span className="line-through text-xs text-muted-foreground">{fmt(1999)}</span>
                  <span className="text-[10px] bg-accent text-white px-2 py-0.5 rounded-full">Save 30%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Marquee */}
        <div className="border-y border-border/50 bg-white/60 backdrop-blur-sm overflow-hidden">
          <div className="marquee-strip flex gap-16 py-3 whitespace-nowrap text-sm">
            {[..."🕉 Free shipping above ₹399 · ✨ Extra 10% off first order — code WELCOME10 · 🚚 Same-day dispatch · 🎁 GST-ready invoices · 🪔 Curated by pandits · 🌸 100% authentic samagri".split("·"), ..."🕉 Free shipping above ₹399 · ✨ Extra 10% off first order — code WELCOME10 · 🚚 Same-day dispatch · 🎁 GST-ready invoices · 🪔 Curated by pandits · 🌸 100% authentic samagri".split("·")].map((t,i)=>(<span key={i} className="text-foreground/80">{t}</span>))}
          </div>
        </div>
      </section>

      {/* KIT OF THE WEEK */}
      {kow && (
        <section className="max-w-7xl mx-auto px-4 pt-14" data-testid="kit-of-week">
          <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-secondary/30 via-white to-accent/10">
            <div className="grid md:grid-cols-[1.2fr_1fr] gap-0 items-stretch">
              <div className="p-8 md:p-12">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary text-white text-xs uppercase tracking-widest font-medium mb-4">✨ Kit of the Week</div>
                <h2 className="font-serif text-3xl md:text-5xl leading-tight text-primary">{kow.featured_title}</h2>
                <p className="mt-4 text-muted-foreground leading-relaxed max-w-md">{kow.featured_description || kow.description}</p>
                <div className="mt-6 flex items-end gap-4">
                  <div className="font-serif text-4xl text-primary">{fmt(kow.price)}</div>
                  {kow.mrp>kow.price && <>
                    <div className="text-lg line-through text-muted-foreground">{fmt(kow.mrp)}</div>
                    <div className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium">Save {fmt(kow.mrp - kow.price)}</div>
                  </>}
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to={`/kit/${kow.slug}`} data-testid="kow-view-btn" className="pk-btn-primary">View Kit · Buy Now</Link>
                  <div className="text-sm text-muted-foreground pt-3">Includes {(kow.items||[]).length}+ curated puja items</div>
                </div>
              </div>
              <div className="relative min-h-[280px] md:min-h-[380px] bg-cover bg-center" style={{backgroundImage:`url(${kow.featured_banner || kow.featured_image || kow.image || '/favicon.svg'})`}}>
                <div className="absolute inset-0 bg-gradient-to-l from-transparent to-white/40 md:to-white/10"/>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SHOP BY CATEGORY */}
      <section className="max-w-7xl mx-auto px-4 pt-16">
        <div className="flex items-end justify-between">
          <div>
            <div className="section-label">Shop by Category</div>
            <h2 className="font-serif text-3xl md:text-4xl mt-2">Everything for your sacred space</h2>
          </div>
          <Link to="/categories" className="text-sm text-primary underline underline-offset-4 hidden sm:inline">View all →</Link>
        </div>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {cats.slice(0, 12).map((c) => (
            <Link key={c.id} to={`/category/${c.slug}`} data-testid={`category-card-${c.slug}`}
              className="pk-card overflow-hidden aspect-square relative group">
              <img src={c.image} alt={c.name} className="w-full h-full object-cover" style={{transition:"transform .5s ease"}} onMouseOver={e=>e.currentTarget.style.transform="scale(1.1)"} onMouseOut={e=>e.currentTarget.style.transform="scale(1)"}/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"/>
              <div className="absolute bottom-3 left-3 right-3">
                <div className="text-white font-medium text-sm leading-tight">{c.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="max-w-7xl mx-auto px-4 pt-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="section-label">Best Sellers</div>
            <h2 className="font-serif text-3xl md:text-4xl mt-2">Loved by devotees across India</h2>
          </div>
          <Link to="/best-sellers" className="text-sm text-primary underline hidden sm:inline">See all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
          {bestsellers.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </section>

      {/* KIT FINDER CTA BAND */}
      <section className="max-w-7xl mx-auto px-4 pt-24">
        <div className="rounded-3xl bg-gradient-to-br from-[#800000] to-[#a91d1d] text-white p-8 md:p-14 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#D4AF37]/20 blur-3xl"/>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#F47920]/20 blur-3xl"/>
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-secondary font-semibold flex items-center gap-2"><Sparkles className="w-3.5 h-3.5"/>Smart Puja Kit Finder</div>
              <h2 className="devanagari font-serif text-4xl md:text-5xl mt-3 leading-tight">पूजा करनी है? पूरी सामग्री हम चुनेंगे।</h2>
              <p className="mt-4 opacity-90 text-lg">Tell us your puja, occasion and family size — we'll curate the perfect kit for you. No more running around to 5 shops.</p>
              <button data-testid="cta-find-kit" onClick={()=>setFinderOpen(true)} className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-primary px-7 py-3.5 font-semibold hover:bg-secondary hover:text-primary transition-colors">
                Find My Puja Kit <Sparkles className="w-4 h-4"/>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {["Ganesh Puja","Lakshmi Puja","Shiv Puja","Griha Pravesh"].map(x => (
                <div key={x} className="rounded-xl bg-white/10 backdrop-blur border border-white/20 p-5">
                  <div className="text-xs uppercase tracking-wider opacity-70">Kit for</div>
                  <div className="font-serif text-xl mt-1">{x}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DAILY ESSENTIALS */}
      <section className="max-w-7xl mx-auto px-4 pt-24">
        <div className="section-label">Daily Puja Essentials</div>
        <h2 className="font-serif text-3xl md:text-4xl mt-2">Everyday devotion, made effortless</h2>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {daily.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </section>

      {/* FESTIVALS */}
      <section className="max-w-7xl mx-auto px-4 pt-24">
        <div className="section-label">Festival Shopping</div>
        <h2 className="font-serif text-3xl md:text-4xl mt-2">Celebrate every festival with the right samagri</h2>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {FESTIVALS.map(f => (
            <Link key={f.slug} to={`/festival/${encodeURIComponent(f.slug)}`} data-testid={`festival-${f.slug}`} className="pk-card overflow-hidden aspect-[3/4] relative group">
              <img src={f.image} alt={f.name} className="w-full h-full object-cover" style={{transition:"transform .5s ease"}} onMouseOver={e=>e.currentTarget.style.transform="scale(1.08)"} onMouseOut={e=>e.currentTarget.style.transform="scale(1)"}/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"/>
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <div className="text-[10px] uppercase tracking-widest opacity-80">Festival</div>
                <div className="font-serif text-xl leading-tight">{f.name}</div>
                <div className="mt-1 text-xs underline">Shop now →</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* DEITIES */}
      <section className="max-w-7xl mx-auto px-4 pt-24">
        <div className="section-label">Shop by Deity</div>
        <h2 className="font-serif text-3xl md:text-4xl mt-2">God & Goddess Collections</h2>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {DEITIES.map(d => (
            <Link key={d.slug} to={`/deity/${d.slug}`} data-testid={`deity-${d.slug}`}
              className="rounded-2xl p-6 bg-gradient-to-br from-white to-muted border border-border hover:border-secondary transition-colors text-center">
              <div className="w-16 h-16 rounded-full mx-auto bg-gradient-to-br from-secondary/30 to-accent/20 grid place-items-center text-2xl font-serif text-primary">ॐ</div>
              <div className="mt-3 font-serif text-lg">{d.name}</div>
              <div className="text-xs text-muted-foreground mt-1">Shop collection →</div>
            </Link>
          ))}
        </div>
      </section>

      {/* TRUST BAND */}
      <section className="max-w-7xl mx-auto px-4 pt-24">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: ShieldCheck, t:"100% Authentic", d:"Purity checked" },
            { icon: Truck, t:"Pan-India Delivery", d:"Fast dispatch" },
            { icon: HandCoins, t:"COD Available", d:"Pay on delivery" },
            { icon: RotateCcw, t:"Easy Returns", d:"7-day policy" },
          ].map((x,i) => (
            <div key={i} className="rounded-2xl bg-white border border-border p-5 flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-secondary/20 text-primary grid place-items-center"><x.icon className="w-5 h-5"/></div>
              <div>
                <div className="font-medium">{x.t}</div>
                <div className="text-xs text-muted-foreground">{x.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 pt-24 pb-8">
        <div className="section-label">From our devoted customers</div>
        <h2 className="font-serif text-3xl md:text-4xl mt-2">1,00,000+ happy pujas & counting</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          {[
            {n:"Priya S., Mumbai", r:"The Diwali kit had everything I needed — no last-minute rush. Packaging is beautiful."},
            {n:"Rohit K., Bengaluru", r:"Rudraksha mala came with certification. Genuine quality, fast delivery."},
            {n:"Sunita M., Delhi", r:"Loved the Puja Kit Finder — recommended exactly what my Griha Pravesh needed."},
          ].map((t,i)=>(
            <div key={i} className="rounded-2xl bg-white border border-border p-6">
              <div className="flex text-secondary">{[...Array(5)].map((_,i)=><Star key={i} className="w-4 h-4 fill-secondary"/>)}</div>
              <p className="mt-4 leading-relaxed">"{t.r}"</p>
              <div className="mt-4 text-sm font-medium text-primary">— {t.n}</div>
            </div>
          ))}
        </div>
      </section>

      <KitFinderModal open={finderOpen} onClose={()=>setFinderOpen(false)} />
    </div>
  );
}
